import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBell, FaClock, FaUsers, FaCircle, FaTimes, FaExpandAlt } from 'react-icons/fa';
import { useCloudflareAuth } from '../../../contexts/CloudflareAuthContext';

interface Notification {
  id: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  status: 'sent' | 'pending' | 'failed';
  targetAudience: string;
  scheduledDate: string;
  createdAt: string;
  isRead?: boolean;
}

// Utility functions
const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'text-red-400';
    case 'medium':
      return 'text-yellow-400';
    case 'low':
      return 'text-green-400';
    default:
      return 'text-slate-400';
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'sent':
      return 'bg-emerald-500/20 text-emerald-400';
    case 'pending':
      return 'bg-yellow-500/20 text-yellow-400';
    case 'failed':
      return 'bg-red-500/20 text-red-400';
    default:
      return 'bg-slate-500/20 text-slate-400';
  }
};

const NotificationModal: React.FC<{
  notification: Notification | null;
  onClose: () => void;
}> = ({ notification, onClose }) => {
  if (!notification) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-slate-800 rounded-2xl w-full max-w-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6 bg-slate-900/50 flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{notification.title}</h2>
              <div className="flex items-center gap-3 mt-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(notification.status)}`}>
                  {notification.status}
                </span>
                <span className={`flex items-center gap-1 text-sm ${getPriorityColor(notification.priority)}`}>
                  <FaCircle className="h-2 w-2" />
                  <span className="capitalize">{notification.priority} Priority</span>
                </span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors">
              <FaTimes className="h-5 w-5 text-slate-400 hover:text-white" />
            </button>
          </div>
          <div className="p-6 space-y-6">
            <p className="text-slate-300 text-lg leading-relaxed">{notification.message}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-900/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <FaClock className="h-4 w-4" />
                  <span className="text-sm font-medium">Scheduled For</span>
                </div>
                <p className="text-white">{new Date(notification.scheduledDate).toLocaleString()}</p>
              </div>
              <div className="bg-slate-900/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <FaUsers className="h-4 w-4" />
                  <span className="text-sm font-medium">Target Audience</span>
                </div>
                <p className="text-white capitalize">{notification.targetAudience}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Notifications = () => {
  const [notifications] = useState<Notification[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const { currentUser } = useCloudflareAuth();

  // Notifications not yet migrated - show empty state
  if (!currentUser) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <p className="text-slate-400">Please login to view notifications</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <FaBell className="h-6 w-6 text-blue-500" />
          Notifications
        </h1>
      </div>

      {notifications.length === 0 ? (
        <div className="bg-slate-800 rounded-xl p-8 text-center">
          <FaBell className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Notifications</h3>
          <p className="text-slate-400">You don't have any notifications yet.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          <AnimatePresence>
            {notifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-slate-800 rounded-xl p-6 hover:bg-slate-800/80 transition-colors ${
                  !notification.isRead ? 'border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white truncate">{notification.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(notification.status)}`}>
                        {notification.status}
                      </span>
                    </div>
                    <p className="text-slate-300 line-clamp-2 mb-3">{notification.message}</p>
                  </div>
                  <button
                    onClick={() => setSelectedNotification(notification)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <FaExpandAlt className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {selectedNotification && (
        <NotificationModal notification={selectedNotification} onClose={() => setSelectedNotification(null)} />
      )}
    </div>
  );
};

export default Notifications;
